import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock,
  User, Phone, MapPin, RefreshCw, ArrowLeft, Edit2, Save, X,
  Plus, Lock, Trash2
} from 'lucide-react';

const STATUS_COLORS = {
  confirmed: { bg: 'bg-emerald-100 border-emerald-400', text: 'text-emerald-800', badge: 'bg-emerald-500' },
  pending: { bg: 'bg-amber-100 border-amber-400', text: 'text-amber-800', badge: 'bg-amber-500' },
  completed: { bg: 'bg-sky-100 border-sky-400', text: 'text-sky-800', badge: 'bg-sky-500' },
  reschedule_pending: { bg: 'bg-orange-100 border-orange-400', text: 'text-orange-800', badge: 'bg-orange-500' },
  blocked: { bg: 'bg-red-100 border-red-400', text: 'text-red-700', badge: 'bg-red-500' },
};

const ARRIVAL_ICONS = {
  none: null, incoming: '🚗', outside: '🚪', at_door: '🔔', arrived: '✅',
};

const GIRL_COLORS = {
  Luana: 'border-l-rose-500',
  Stella: 'border-l-violet-500',
};

function formatTime(isoStr) {
  if (!isoStr) return '--:--';
  try {
    return new Date(isoStr).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return '--:--'; }
}

function formatDateFull(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

// Use LOCAL date, not UTC — fixes cross-midnight timezone bug
function dateToYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

// ─── Appointment Block ───
function ApptBlock({ appt, onClick }) {
  const { t } = useTranslation();
  const sc = STATUS_COLORS[appt.status] || STATUS_COLORS.confirmed;
  const girlColor = GIRL_COLORS[appt.girl_name] || 'border-l-teal-500';
  const arrival = ARRIVAL_ICONS[appt.arrival_status];
  const isBlocked = appt.status === 'blocked';
  const isPast = appt.end_at ? new Date(appt.end_at) < new Date() : false;

  return (
    <button
      data-testid={`appt-block-${appt.id}`}
      onClick={() => onClick(appt)}
      className={`h-full w-full text-left rounded-md border-l-4 ${girlColor} ${sc.bg} border px-2 py-1
                  hover:shadow-md transition-shadow cursor-pointer text-xs overflow-hidden
                  ${isPast ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className={`font-semibold ${sc.text}`}>
          {formatTime(appt.start_at)}-{formatTime(appt.end_at)}
        </span>
        {isBlocked && <Lock className="h-3 w-3 text-red-500" />}
        {arrival && <span className="text-sm">{arrival}</span>}
      </div>
      <div className={`${sc.text} truncate`}>
        {isBlocked ? (
          <>{appt.girl_name || '-'} - {t('calendar.blocked_label')}</>
        ) : (
          <>{appt.girl_name || '-'} - {appt.duration_minutes}min</>
        )}
      </div>
      {isBlocked && appt.notes && (
        <div className="text-red-500 truncate text-[10px] mt-0.5">{appt.notes}</div>
      )}
    </button>
  );
}

// ─── Appointment Detail / Edit Dialog ───
function ApptDialog({ appt, open, onClose, onSave, onDelete, girls, durations }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appt) {
      setForm({
        girl_name: appt.girl_name || '',
        start_at: appt.start_at ? appt.start_at.slice(0, 16) : '',
        duration_minutes: appt.duration_minutes || 30,
        status: appt.status || 'confirmed',
        arrival_status: appt.arrival_status || 'none',
        buzzer_name: appt.buzzer_name || '',
      });
      setEditing(false);
    }
  }, [appt]);

  if (!appt) return null;
  const sc = STATUS_COLORS[appt.status] || STATUS_COLORS.confirmed;
  const isBlocked = appt.status === 'blocked';

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {};
      if (form.girl_name !== appt.girl_name) payload.girl_name = form.girl_name;
      if (form.start_at && form.start_at !== (appt.start_at || '').slice(0, 16)) {
        payload.start_at = form.start_at.includes(':') && form.start_at.split(':').length === 2
          ? form.start_at + ':00'
          : form.start_at;
      }
      if (Number(form.duration_minutes) !== appt.duration_minutes) payload.duration_minutes = Number(form.duration_minutes);
      if (form.status !== appt.status) payload.status = form.status;
      if (form.arrival_status !== appt.arrival_status) payload.arrival_status = form.arrival_status;
      if (form.buzzer_name !== (appt.buzzer_name || '')) payload.buzzer_name = form.buzzer_name;

      if (Object.keys(payload).length === 0) {
        toast.info(t('calendar.noChanges'));
        setEditing(false);
        setSaving(false);
        return;
      }
      await onSave(appt.id, payload);
      setEditing(false);
      toast.success(t('calendar.appointmentUpdated'));
    } catch (e) {
      toast.error(t('common.error') + ': ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleUnblock() {
    setSaving(true);
    try {
      await onDelete(appt.id);
      toast.success(t('calendar.deleted'));
    } catch (e) {
      toast.error(t('common.error') + ': ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="appt-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isBlocked ? <Lock className="h-5 w-5 text-red-500" /> : <CalendarIcon className="h-5 w-5 text-teal-600" />}
            {isBlocked ? t('calendar.blockedSlot') : t('calendar.appointmentDetail')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${sc.badge} text-white`}>{appt.status}</Badge>
            {appt.arrival_status !== 'none' && (
              <Badge variant="outline" className="gap-1">
                {ARRIVAL_ICONS[appt.arrival_status]} {appt.arrival_status}
              </Badge>
            )}
            {appt.source && (
              <Badge variant="outline" className="text-xs">{appt.source}</Badge>
            )}
          </div>

          {!editing ? (
            <div className="grid grid-cols-[100px_1fr] gap-y-3 text-sm">
              <span className="text-slate-500 flex items-center gap-1"><User className="h-3.5 w-3.5" />{t('calendar.girl')}</span>
              <span className="font-medium">{appt.girl_name || '-'}</span>

              <span className="text-slate-500 flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" />{t('calendar.dateLabel')}</span>
              <span className="font-medium">{formatDateFull(appt.start_at)}</span>

              <span className="text-slate-500 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{t('calendar.time')}</span>
              <span className="font-medium">{formatTime(appt.start_at)} - {formatTime(appt.end_at)}</span>

              <span className="text-slate-500">{t('calendar.duration')}</span>
              <span className="font-medium">{appt.duration_minutes} min</span>

              {!isBlocked && (
                <>
                  <span className="text-slate-500 flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{t('calendar.whatsappLabel')}</span>
                  <span className="font-medium font-mono text-xs">{appt.client_phone || '-'}</span>

                  <span className="text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{t('calendar.buzzer')}</span>
                  <span className="font-medium">{appt.buzzer_name || '-'}</span>
                </>
              )}

              {isBlocked && appt.notes && (
                <>
                  <span className="text-slate-500">{t('calendar.notes')}</span>
                  <span className="font-medium">{appt.notes}</span>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>{t('calendar.girl')}</Label>
                <Select value={form.girl_name} onValueChange={v => setForm(f => ({ ...f, girl_name: v }))}>
                  <SelectTrigger data-testid="edit-girl-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {girls.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('calendar.dateTime')}</Label>
                <Input type="datetime-local" value={form.start_at} data-testid="edit-start-at"
                  onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))} />
              </div>
              <div>
                <Label>{t('calendar.duration')} (min)</Label>
                <Select value={String(form.duration_minutes)} onValueChange={v => setForm(f => ({ ...f, duration_minutes: v }))}>
                  <SelectTrigger data-testid="edit-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {durations.map(d => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!isBlocked && (
                <>
                  <div>
                    <Label>{t('calendar.status')}</Label>
                    <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['pending', 'confirmed', 'reschedule_pending', 'completed'].map(s =>
                          <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('calendar.arrivalStatus')}</Label>
                    <Select value={form.arrival_status} onValueChange={v => setForm(f => ({ ...f, arrival_status: v }))}>
                      <SelectTrigger data-testid="edit-arrival-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['none', 'incoming', 'outside', 'at_door', 'arrived'].map(s =>
                          <SelectItem key={s} value={s}>{ARRIVAL_ICONS[s] || '-'} {s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('calendar.buzzer')}</Label>
                    <Input value={form.buzzer_name} data-testid="edit-buzzer"
                      onChange={e => setForm(f => ({ ...f, buzzer_name: e.target.value }))} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!editing ? (
            <div className="flex items-center justify-between w-full">
              <Button variant="destructive" size="sm" onClick={handleUnblock} disabled={saving} data-testid="delete-appt-btn">
                <Trash2 className="h-4 w-4 mr-1" /> {saving ? '...' : t('common.delete')}
              </Button>
              {!isBlocked && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-appt-btn">
                  <Edit2 className="h-4 w-4 mr-1" /> {t('common.edit')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                <X className="h-4 w-4 mr-1" /> {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-appt-btn"
                className="bg-teal-600 hover:bg-teal-700 text-white">
                <Save className="h-4 w-4 mr-1" /> {saving ? t('common.saving') : t('common.save')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Create Appointment Dialog ───
function CreateApptDialog({ open, onClose, onCreate, girls, defaultDate, durations }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const now = new Date();
      const mins = now.getMinutes();
      now.setMinutes(mins < 30 ? 30 : 0);
      if (mins >= 30) now.setHours(now.getHours() + 1);
      const dtLocal = defaultDate
        ? `${dateToYMD(defaultDate)}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        : '';
      setForm({
        girl_name: girls[0] || '',
        start_at: dtLocal,
        duration_minutes: String(durations[0] || 30),
        client_phone: '',
        language: '',
        buzzer_name: '',
      });
    }
  }, [open, girls, defaultDate, durations]);

  async function handleCreate() {
    if (!form.girl_name || !form.start_at) {
      toast.error(t('calendar.girlRequired'));
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        girl_name: form.girl_name,
        start_at: form.start_at.includes(':') && form.start_at.split(':').length === 2
          ? form.start_at + ':00'
          : form.start_at,
        duration_minutes: Number(form.duration_minutes),
        client_phone: form.client_phone || undefined,
        language: form.language || undefined,
        buzzer_name: form.buzzer_name || undefined,
        source: 'manual',
        status: 'confirmed',
      });
      toast.success(t('calendar.appointmentCreated'));
      onClose();
    } catch (e) {
      toast.error(t('common.error') + ': ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="create-appt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-teal-600" />
            {t('calendar.newAppointmentTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>{t('calendar.girl')} *</Label>
            <Select value={form.girl_name} onValueChange={v => setForm(f => ({ ...f, girl_name: v }))}>
              <SelectTrigger data-testid="create-girl-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {girls.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('calendar.dateTime')} *</Label>
            <Input type="datetime-local" value={form.start_at} data-testid="create-start-at"
              onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))} />
          </div>
          <div>
            <Label>{t('calendar.duration')}</Label>
            <Select value={form.duration_minutes} onValueChange={v => setForm(f => ({ ...f, duration_minutes: v }))}>
              <SelectTrigger data-testid="create-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {durations.map(d => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('calendar.clientPhone')} ({t('common.optional')})</Label>
            <Input value={form.client_phone} placeholder="+41..." data-testid="create-phone"
              onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} />
          </div>
          <div>
            <Label>{t('calendar.languageOptional')} ({t('common.optional')})</Label>
            <Select value={form.language || 'none'} onValueChange={v => setForm(f => ({ ...f, language: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="create-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-</SelectItem>
                <SelectItem value="it">{t('languages.it')}</SelectItem>
                <SelectItem value="de">{t('languages.de')}</SelectItem>
                <SelectItem value="en">{t('languages.en')}</SelectItem>
                <SelectItem value="es">{t('languages.es')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('calendar.buzzerOptional')} ({t('common.optional')})</Label>
            <Input value={form.buzzer_name} data-testid="create-buzzer"
              onChange={e => setForm(f => ({ ...f, buzzer_name: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={saving} data-testid="create-appt-submit"
            className="bg-teal-600 hover:bg-teal-700 text-white">
            <Plus className="h-4 w-4 mr-1" /> {saving ? t('calendar.creating') : t('calendar.createAppointment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Block Slot Dialog ───
function BlockSlotDialog({ open, onClose, onCreate, girls, defaultDate, durations }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const now = new Date();
      const mins = now.getMinutes();
      now.setMinutes(mins < 30 ? 30 : 0);
      if (mins >= 30) now.setHours(now.getHours() + 1);
      const dtLocal = defaultDate
        ? `${dateToYMD(defaultDate)}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
        : '';
      setForm({
        girl_name: girls[0] || '',
        start_at: dtLocal,
        duration_minutes: '60',
        notes: '',
      });
    }
  }, [open, girls, defaultDate]);

  async function handleBlock() {
    if (!form.girl_name || !form.start_at) {
      toast.error(t('calendar.girlRequired'));
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        girl_name: form.girl_name,
        start_at: form.start_at.includes(':') && form.start_at.split(':').length === 2
          ? form.start_at + ':00'
          : form.start_at,
        duration_minutes: Number(form.duration_minutes),
        source: 'manual_block',
        status: 'blocked',
        notes: form.notes || undefined,
      });
      toast.success(t('calendar.slotBlocked'));
      onClose();
    } catch (e) {
      toast.error(t('common.error') + ': ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="block-slot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-red-500" />
            {t('calendar.blockSlotTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>{t('calendar.girl')} *</Label>
            <Select value={form.girl_name} onValueChange={v => setForm(f => ({ ...f, girl_name: v }))}>
              <SelectTrigger data-testid="block-girl-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {girls.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('calendar.dateTime')} *</Label>
            <Input type="datetime-local" value={form.start_at} data-testid="block-start-at"
              onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))} />
          </div>
          <div>
            <Label>{t('calendar.duration')}</Label>
            <Select value={form.duration_minutes} onValueChange={v => setForm(f => ({ ...f, duration_minutes: v }))}>
              <SelectTrigger data-testid="block-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...new Set([...durations, 120])].sort((a, b) => a - b).map(d =>
                  <SelectItem key={d} value={String(d)}>{d} min</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('calendar.blockReason')} ({t('common.optional')})</Label>
            <Textarea value={form.notes} placeholder={t('calendar.blockReasonPlaceholder')} data-testid="block-notes"
              rows={2} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <Button variant="destructive" onClick={handleBlock} disabled={saving} data-testid="block-slot-submit">
            <Lock className="h-4 w-4 mr-1" /> {saving ? t('calendar.blocking') : t('calendar.blockSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Overlap Layout Algorithm ───
function computeOverlapLayout(appts) {
  if (!appts.length) return [];

  const sorted = [...appts].sort((a, b) => {
    const diff = new Date(a.start_at) - new Date(b.start_at);
    if (diff !== 0) return diff;
    const endA = a.end_at ? new Date(a.end_at) : new Date(new Date(a.start_at).getTime() + (a.duration_minutes || 30) * 60000);
    const endB = b.end_at ? new Date(b.end_at) : new Date(new Date(b.start_at).getTime() + (b.duration_minutes || 30) * 60000);
    return endA - endB;
  });

  function getEnd(appt) {
    if (appt.end_at) return new Date(appt.end_at).getTime();
    return new Date(appt.start_at).getTime() + (appt.duration_minutes || 30) * 60000;
  }

  const result = new Map();
  let i = 0;

  while (i < sorted.length) {
    // Build connected group of overlapping events
    const group = [sorted[i]];
    let groupEnd = getEnd(sorted[i]);
    let j = i + 1;

    while (j < sorted.length && new Date(sorted[j].start_at).getTime() < groupEnd) {
      group.push(sorted[j]);
      groupEnd = Math.max(groupEnd, getEnd(sorted[j]));
      j++;
    }

    // Assign columns within this group using greedy left-packing
    const columnEnds = [];
    for (const appt of group) {
      const start = new Date(appt.start_at).getTime();
      let placed = false;
      for (let c = 0; c < columnEnds.length; c++) {
        if (columnEnds[c] <= start) {
          columnEnds[c] = getEnd(appt);
          result.set(appt.id, { column: c });
          placed = true;
          break;
        }
      }
      if (!placed) {
        result.set(appt.id, { column: columnEnds.length });
        columnEnds.push(getEnd(appt));
      }
    }

    const totalColumns = columnEnds.length;
    for (const appt of group) {
      result.get(appt.id).totalColumns = totalColumns;
    }

    i = j;
  }

  return sorted.map(appt => ({
    appt,
    column: result.get(appt.id).column,
    totalColumns: result.get(appt.id).totalColumns,
  }));
}

// ─── Day View (timeline with overlap columns) ───
const HOUR_HEIGHT_PX = 60;

function DayView({ appointments, date, onClickAppt }) {
  const { t } = useTranslation();
  const dayStr = dateToYMD(date);
  const dayAppts = appointments.filter(a => a.start_at && a.start_at.startsWith(dayStr));
  const firstDayApptStart = dayAppts[0]?.start_at || null;
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (!scrollRef.current) return;
    const isToday = dayStr === dateToYMD(new Date());
    let targetHour = 8;
    if (isToday) {
      targetHour = Math.max(0, new Date().getHours() - 1);
    } else if (firstDayApptStart) {
      targetHour = Math.max(0, new Date(firstDayApptStart).getHours() - 1);
    }
    scrollRef.current.scrollTop = targetHour * HOUR_HEIGHT_PX;
  }, [dayStr, firstDayApptStart]);

  const layoutAppts = computeOverlapLayout(dayAppts);

  function timeToY(isoStr) {
    const d = new Date(isoStr);
    return (d.getHours() + d.getMinutes() / 60) * HOUR_HEIGHT_PX;
  }

  function getEndY(appt) {
    if (appt.end_at) return timeToY(appt.end_at);
    return timeToY(appt.start_at) + ((appt.duration_minutes || 30) / 60) * HOUR_HEIGHT_PX;
  }

  return (
    <div className="space-y-0" data-testid="day-view">
      <h3 className="text-sm font-semibold text-slate-700 mb-3" data-testid="day-view-date">
        {formatDateFull(date.toISOString())}
      </h3>
      <div className="relative max-h-[600px] overflow-y-auto" ref={scrollRef}>
        <div className="flex" style={{ height: 24 * HOUR_HEIGHT_PX }}>
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0 relative">
            {HOURS.map(h => (
              <div key={h} className="absolute text-xs text-slate-400 font-mono"
                   style={{ top: h * HOUR_HEIGHT_PX }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Events area */}
          <div className="flex-1 relative" data-testid="day-view-events-area">
            {/* Hour grid lines */}
            {HOURS.map(h => (
              <div key={h} className="absolute left-0 right-0 border-b border-slate-100"
                   style={{ top: h * HOUR_HEIGHT_PX, height: HOUR_HEIGHT_PX }} />
            ))}

            {/* Appointment blocks with overlap columns */}
            {layoutAppts.map(({ appt, column, totalColumns }) => {
              const top = timeToY(appt.start_at);
              const height = Math.max(getEndY(appt) - top, 24);
              const widthPercent = 100 / totalColumns;
              const leftPercent = column * widthPercent;

              return (
                <div key={appt.id} className="absolute px-0.5"
                     data-testid={`appt-lane-${appt.id}`}
                     style={{
                       top: `${top}px`,
                       height: `${height}px`,
                       left: `${leftPercent}%`,
                       width: `${widthPercent}%`,
                       zIndex: 10,
                     }}>
                  <ApptBlock appt={appt} onClick={onClickAppt} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {dayAppts.length === 0 && (
        <p className="text-center text-slate-400 text-sm py-8">{t('calendar.noAppointmentsDay')}</p>
      )}
    </div>
  );
}

// ─── Main Calendar Page ───
export default function CalendarPage() {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [girlFilter, setGirlFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showBlock, setShowBlock] = useState(false);

  const durations = (Array.isArray(settings?.durations) && settings.durations.length > 0)
    ? settings.durations
    : [30, 60, 90];

  const girls = settings?.girls_list || ['Luana', 'Stella'];
  const fetchAppointments = useCallback(async () => {
    try {
      const params = { date: dateToYMD(currentDate) };
      if (girlFilter !== 'all') params.girl = girlFilter;
      if (statusFilter !== 'all') params.status = statusFilter;

      const res = await api.get('/appointments', { params });
      setAppointments(res.data.appointments || []);
    } catch (e) {
      toast.error(t('calendar.loadError'));
    } finally {
      setLoading(false);
    }
  }, [currentDate, girlFilter, statusFilter, t]);

  useEffect(() => {
    setLoading(true);
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    async function loadSettings() {
      try {
        const res = await api.get('/tenant/settings');
        setSettings(res.data);
      } catch (e) { console.warn('[Calendar] Failed to load settings:', e); }
    }
    loadSettings();
  }, []);

  function navigateDate(dir) {
    setCurrentDate(prev => addDays(prev, dir));
  }

  function goToday() {
    setCurrentDate(new Date());
  }

  async function handleSaveAppt(id, payload) {
    await api.put(`/appointments/${id}`, payload);
    await fetchAppointments();
    setSelectedAppt(null);
  }

  async function handleDeleteAppt(id) {
    await api.delete(`/appointments/${id}`);
    await fetchAppointments();
    setSelectedAppt(null);
  }

  async function handleCreateAppt(payload) {
    await api.post('/appointments', payload);
    await fetchAppointments();
  }

  // Counts (operational only — no cancelled, no blocked in "confirmed" count)
  const confirmed = appointments.filter(a => a.status === 'confirmed').length;
  const pending = appointments.filter(a => a.status === 'pending').length;
  const blocked = appointments.filter(a => a.status === 'blocked').length;
  const arriving = appointments.filter(a => ['incoming', 'outside', 'at_door'].includes(a.arrival_status)).length;

  const statsText = [
    confirmed > 0 && `${confirmed} ${t('calendar.confirmed')}`,
    pending > 0 && `${pending} ${t('calendar.pending')}`,
    blocked > 0 && `${blocked} ${t('calendar.blocked')}`,
    arriving > 0 && `${arriving} ${t('calendar.arriving')}`,
  ].filter(Boolean).join(' - ') || t('calendar.noAppointments');

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="calendar-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('calendar.title')}
              </h1>
              <p className="text-xs text-slate-400" data-testid="calendar-stats">{statsText}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchAppointments(); }}
              data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
        {/* Date nav */}
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateDate(-1)}
            data-testid="prev-date">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToday} data-testid="today-btn"
            className="text-xs font-medium text-teal-700">
            {t('common.today')}
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateDate(1)}
            data-testid="next-date">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-appt-btn"
          className="bg-teal-600 hover:bg-teal-700 text-white">
          <Plus className="h-4 w-4 mr-1" /> {t('calendar.newAppointment')}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowBlock(true)} data-testid="block-slot-btn"
          className="text-red-600 border-red-200 hover:bg-red-50">
          <Lock className="h-4 w-4 mr-1" /> {t('calendar.blockSlot')}
        </Button>

        <div className="w-px h-6 bg-slate-200" />

        {/* Girl filter */}
        <Select value={girlFilter} onValueChange={setGirlFilter}>
          <SelectTrigger className="w-[110px] h-8 text-xs" data-testid="girl-filter">
            <SelectValue placeholder={t('calendar.girlFilter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calendar.allGirls')}</SelectItem>
            {girls.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[110px] h-8 text-xs" data-testid="status-filter">
            <SelectValue placeholder={t('calendar.statusFilter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calendar.activeFilter')}</SelectItem>
            <SelectItem value="confirmed">{t('calendar.confirmedFilter')}</SelectItem>
            <SelectItem value="pending">{t('calendar.pendingFilter')}</SelectItem>
            <SelectItem value="blocked">{t('calendar.blockedFilter')}</SelectItem>
            <SelectItem value="completed">{t('calendar.completedFilter')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Calendar content */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            {loading ? (
              <div className="flex justify-center py-12" data-testid="calendar-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
              </div>
            ) : (
              <DayView appointments={appointments} date={currentDate} onClickAppt={setSelectedAppt} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail / Edit dialog */}
      <ApptDialog
        appt={selectedAppt}
        open={!!selectedAppt}
        onClose={() => setSelectedAppt(null)}
        onSave={handleSaveAppt}
        onDelete={handleDeleteAppt}
        girls={girls}
        durations={durations}
      />

      {/* Create Appointment dialog */}
      <CreateApptDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreateAppt}
        girls={girls}
        defaultDate={currentDate}
        durations={durations}
      />

      {/* Block Slot dialog */}
      <BlockSlotDialog
        open={showBlock}
        onClose={() => setShowBlock(false)}
        onCreate={handleCreateAppt}
        girls={girls}
        defaultDate={currentDate}
        durations={durations}
      />
    </div>
  );
}
